import { useState, useEffect, useCallback } from 'react';

export interface UseOTPRetryReturn {
  totalAttemptsRemaining: number;
  isInCooldown: boolean;
  cooldownTimeRemaining: number;
  canAttemptOTP: boolean;
  recordOTPAttempt: () => void;
  resetAttempts: () => void;
  formatCooldownTime: (seconds: number) => string;
}

export interface UseOTPRetryOptions {
  maxTotalAttempts?: number;
  cooldownDuration?: number; // in seconds
  storageKey?: string;
  phoneNumber?: string; // Current phone number to track attempts for
}

/**
 * Custom hook for managing OTP attempts (sending + verification) and cooldown periods
 * @param options Configuration options for retry logic
 * @returns Retry state and control functions
 */
export const useOTPRetry = (options: UseOTPRetryOptions = {}): UseOTPRetryReturn => {
  const { 
    maxTotalAttempts = 5, 
    cooldownDuration = 900, // 15 minutes in seconds
    storageKey = 'otp_retry_data',
    phoneNumber
  } = options;
  
  const [totalAttemptsRemaining, setTotalAttemptsRemaining] = useState(maxTotalAttempts);
  const [isInCooldown, setIsInCooldown] = useState(false);
  const [cooldownTimeRemaining, setCooldownTimeRemaining] = useState(0);

  // Load saved retry data from localStorage on mount and when phone number changes
  useEffect(() => {
    if (!phoneNumber) {
      // Reset to default values when no phone number
      setTotalAttemptsRemaining(maxTotalAttempts);
      setIsInCooldown(false);
      setCooldownTimeRemaining(0);
      return;
    }

    try {
      const savedData = localStorage.getItem(storageKey);
      if (savedData) {
        const allPhoneData = JSON.parse(savedData);
        const phoneData = allPhoneData[phoneNumber];
        
        if (phoneData) {
          const { totalAttempts, cooldownEndTime } = phoneData;
          const now = Date.now();
          
          if (cooldownEndTime && cooldownEndTime > now) {
            // Still in cooldown period
            setIsInCooldown(true);
            setCooldownTimeRemaining(Math.ceil((cooldownEndTime - now) / 1000));
            setTotalAttemptsRemaining(0);
          } else if (cooldownEndTime && cooldownEndTime <= now) {
            // Cooldown period has ended, reset attempts
            setTotalAttemptsRemaining(maxTotalAttempts);
            setIsInCooldown(false);
            setCooldownTimeRemaining(0);
            // Remove this phone number's data
            const updatedData = { ...allPhoneData };
            delete updatedData[phoneNumber];
            localStorage.setItem(storageKey, JSON.stringify(updatedData));
          } else {
            // No cooldown, use saved attempts
            setTotalAttemptsRemaining(totalAttempts || maxTotalAttempts);
            setIsInCooldown(false);
            setCooldownTimeRemaining(0);
          }
        } else {
          // No data for this phone number, reset to default
          setTotalAttemptsRemaining(maxTotalAttempts);
          setIsInCooldown(false);
          setCooldownTimeRemaining(0);
        }
      } else {
        // No saved data at all, reset to default
        setTotalAttemptsRemaining(maxTotalAttempts);
        setIsInCooldown(false);
        setCooldownTimeRemaining(0);
      }
    } catch (error) {
      console.error('Error loading OTP retry data:', error);
      // Reset to default values on error
      setTotalAttemptsRemaining(maxTotalAttempts);
      setIsInCooldown(false);
      setCooldownTimeRemaining(0);
    }
  }, [maxTotalAttempts, storageKey, phoneNumber]);

  // Cooldown timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isInCooldown && cooldownTimeRemaining > 0 && phoneNumber) {
      interval = setInterval(() => {
        setCooldownTimeRemaining((prev) => {
          if (prev <= 1) {
            // Cooldown period ended
            setIsInCooldown(false);
            setTotalAttemptsRemaining(maxTotalAttempts);
            // Remove this phone number's data
            try {
              const savedData = localStorage.getItem(storageKey);
              if (savedData) {
                const allPhoneData = JSON.parse(savedData);
                const updatedData = { ...allPhoneData };
                delete updatedData[phoneNumber];
                localStorage.setItem(storageKey, JSON.stringify(updatedData));
              }
            } catch (error) {
              console.error('Error removing phone data:', error);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isInCooldown, cooldownTimeRemaining, maxTotalAttempts, storageKey, phoneNumber]);

  /**
   * Record an OTP attempt (sending or verification failure)
   */
  const recordOTPAttempt = useCallback(() => {
    if (!phoneNumber) return;
    
    const newTotalAttemptsRemaining = totalAttemptsRemaining - 1;
    setTotalAttemptsRemaining(newTotalAttemptsRemaining);

    try {
      // Load existing data
      const savedData = localStorage.getItem(storageKey);
      const allPhoneData = savedData ? JSON.parse(savedData) : {};
      
      if (newTotalAttemptsRemaining <= 0) {
        // Start cooldown period
        const cooldownEndTime = Date.now() + (cooldownDuration * 1000);
        setIsInCooldown(true);
        setCooldownTimeRemaining(cooldownDuration);
        
        // Save to localStorage for this phone number
        allPhoneData[phoneNumber] = {
          totalAttempts: 0,
          cooldownEndTime
        };
      } else {
        // Save remaining attempts
        allPhoneData[phoneNumber] = {
          totalAttempts: newTotalAttemptsRemaining,
          cooldownEndTime: null
        };
      }
      
      localStorage.setItem(storageKey, JSON.stringify(allPhoneData));
    } catch (error) {
      console.error('Error saving OTP retry data:', error);
    }
  }, [totalAttemptsRemaining, cooldownDuration, storageKey, phoneNumber]);

  /**
   * Reset attempts (called when changing phone number or after cooldown)
   */
  const resetAttempts = useCallback(() => {
    setTotalAttemptsRemaining(maxTotalAttempts);
    setIsInCooldown(false);
    setCooldownTimeRemaining(0);
    
    if (phoneNumber) {
      try {
        // Remove this phone number's data
        const savedData = localStorage.getItem(storageKey);
        if (savedData) {
          const allPhoneData = JSON.parse(savedData);
          const updatedData = { ...allPhoneData };
          delete updatedData[phoneNumber];
          localStorage.setItem(storageKey, JSON.stringify(updatedData));
        }
      } catch (error) {
        console.error('Error removing phone data:', error);
      }
    }
  }, [maxTotalAttempts, storageKey, phoneNumber]);

  /**
   * Format cooldown time into MM:SS format
   * @param seconds Number of seconds
   * @returns Formatted time string (e.g., "15:00", "1:30")
   */
  const formatCooldownTime = useCallback((seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  const canAttemptOTP = totalAttemptsRemaining > 0 && !isInCooldown;

  return {
    totalAttemptsRemaining,
    isInCooldown,
    cooldownTimeRemaining,
    canAttemptOTP,
    recordOTPAttempt,
    resetAttempts,
    formatCooldownTime,
  };
};
